import {
    computed,
    Injectable,
    signal
} from '@angular/core';

import {
    LoginResponse
} from '../models/login-response';

import {
    MeResponse
} from '../models/me-response';


@Injectable({
    providedIn: 'root'
})
export class SessionService {

    private readonly TOKEN_KEY =
        'restaurantos_access_token';

    private readonly TOKEN_TYPE_KEY =
        'restaurantos_token_type';

    private readonly EXPIRES_AT_KEY =
        'restaurantos_expires_at';

    private readonly USER_KEY =
        'restaurantos_user';


    /*
     * =========================================================
     * REACTIVE STATE
     * =========================================================
     */

    private readonly currentUserSignal =
        signal<MeResponse | null>(
            this.readUserFromStorage()
        );


    readonly currentUser =
        this.currentUserSignal.asReadonly();


    readonly authenticated =
        computed(() => {

            /*
             * Dependemos también del usuario para que Angular
             * conozca el estado reactivo de la sesión.
             */
            this.currentUserSignal();

            return this.hasValidSession();
        });


    readonly displayName =
        computed(() => {

            const user =
                this.currentUserSignal();

            return user?.displayName
                || user?.email
                || 'Usuario';
        });


    readonly roles =
        computed(() =>
            this.currentUserSignal()?.roles ?? []
        );


    /*
     * =========================================================
     * SESSION
     * =========================================================
     */

    startSession(
        response: LoginResponse,
        rememberMe: boolean
    ): void {

        this.clear();


        const storage: Storage =
            rememberMe
                ? localStorage
                : sessionStorage;


        const expiresAt =
            Date.now() +
            response.expiresIn * 1000;


        storage.setItem(
            this.TOKEN_KEY,
            response.accessToken
        );


        storage.setItem(
            this.TOKEN_TYPE_KEY,
            response.tokenType
        );


        storage.setItem(
            this.EXPIRES_AT_KEY,
            expiresAt.toString()
        );
    }


    setUser(
        user: MeResponse
    ): void {

        const storage =
            this.getActiveStorage();


        if (storage === null) {
            return;
        }


        storage.setItem(
            this.USER_KEY,
            JSON.stringify(user)
        );


        /*
         * Esto provoca que cualquier componente
         * que consuma currentUser() se actualice
         * inmediatamente.
         */
        this.currentUserSignal.set(
            user
        );
    }


    getAccessToken():
        string | null {

        const localToken =
            localStorage.getItem(
                this.TOKEN_KEY
            );


        if (localToken !== null) {
            return localToken;
        }


        return sessionStorage.getItem(
            this.TOKEN_KEY
        );
    }


    getUser():
        MeResponse | null {

        return this.currentUserSignal();
    }


    isAuthenticated():
        boolean {

        return this.hasValidSession();
    }


    hasRole(
        role: string
    ): boolean {

        return this.roles()
            .includes(role);
    }


    clear(): void {

        const keys: string[] = [

            this.TOKEN_KEY,

            this.TOKEN_TYPE_KEY,

            this.EXPIRES_AT_KEY,

            this.USER_KEY
        ];


        for (const key of keys) {

            localStorage.removeItem(
                key
            );

            sessionStorage.removeItem(
                key
            );
        }


        /*
         * Actualización reactiva inmediata.
         */
        this.currentUserSignal.set(
            null
        );
    }


    /*
     * =========================================================
     * PRIVATE
     * =========================================================
     */

    private hasValidSession():
        boolean {

        const storage =
            this.getActiveStorage();


        if (storage === null) {
            return false;
        }


        const token =
            storage.getItem(
                this.TOKEN_KEY
            );


        const expiresAt =
            storage.getItem(
                this.EXPIRES_AT_KEY
            );


        if (
            token === null ||
            expiresAt === null
        ) {

            return false;
        }


        const expiration =
            Number(expiresAt);


        if (
            Number.isNaN(expiration) ||
            Date.now() >= expiration
        ) {

            this.clear();

            return false;
        }


        return true;
    }


    private getActiveStorage():
        Storage | null {

        if (
            localStorage.getItem(
                this.TOKEN_KEY
            ) !== null
        ) {

            return localStorage;
        }


        if (
            sessionStorage.getItem(
                this.TOKEN_KEY
            ) !== null
        ) {

            return sessionStorage;
        }


        return null;
    }


    private readUserFromStorage():
        MeResponse | null {

        const rawUser =
            localStorage.getItem(
                this.USER_KEY
            )
            ??
            sessionStorage.getItem(
                this.USER_KEY
            );


        if (rawUser === null) {
            return null;
        }


        try {

            const parsed: unknown =
                JSON.parse(rawUser);


            if (
                this.isMeResponse(
                    parsed
                )
            ) {

                return parsed;
            }


            return null;

        } catch {

            return null;
        }
    }


    private isMeResponse(
        value: unknown
    ): value is MeResponse {

        if (
            typeof value !== 'object' ||
            value === null
        ) {

            return false;
        }


        if (
            !('id' in value) ||
            typeof value.id !== 'string'
        ) {

            return false;
        }


        if (
            !('email' in value) ||
            typeof value.email !== 'string'
        ) {

            return false;
        }


        if (
            !('displayName' in value) ||
            typeof value.displayName !== 'string'
        ) {

            return false;
        }


        if (
            !('roles' in value) ||
            !Array.isArray(value.roles)
        ) {

            return false;
        }


        return value.roles.every(
            (role: unknown) =>
                typeof role === 'string'
        );
    }
}